/**
 * Tiny helpers for sign-in / forgot-password identifier handling.
 *
 * Detect which kind of identifier the user is entering — email, phone,
 * or username — based on the shape of the string. The result drives
 * the input's keyboard mode, autocomplete attribute, and the small
 * "kind" badge that appears on the field for confidence.
 *
 * No backend contract changes — the existing /authenticate endpoint
 * accepts `username` as a single field and looks the user up by any
 * of the three. We just send a hint alongside (`identifierType`) that
 * the backend can opt into reading, ignored otherwise.
 */

// Email — pragmatic RFC-lite that catches the cases people actually type.
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

// Phone — accepts +country prefix, spaces, dashes, parens, dots.
// Minimum 7 digits to avoid catching short usernames.
const PHONE_RE = /^[+]?[0-9][0-9\s().-]{5,}[0-9]$/;

// Loose "looks like a phone" check for in-progress typing.
const PHONE_LOOSE_RE = /^[+]?[\d\s().-]+$/;

/**
 * @param {string} value
 * @returns {"email"|"phone"|"username"|"empty"}
 */
export function identifierKind(value) {
  const v = (value || "").trim();
  if (!v) return "empty";
  if (EMAIL_RE.test(v)) return "email";
  if (PHONE_RE.test(v)) return "phone";
  if (v.includes("@")) return "email";   // user is typing an email
  if (PHONE_LOOSE_RE.test(v) && v.replace(/\D/g, "").length >= 4) return "phone";
  return "username";
}

/**
 * @param {string} value
 * @returns {"email"|"phone"|"username"|"empty"} — same as identifierKind
 * but only commits to email/phone once the value is fully valid.
 */
export function identifierKindStrict(value) {
  const v = (value || "").trim();
  if (!v) return "empty";
  if (EMAIL_RE.test(v)) return "email";
  if (PHONE_RE.test(v)) return "phone";
  return "username";
}

/**
 * Strip phone formatting so we send a canonical value to the backend.
 * Keeps the leading +, drops everything else but digits.
 */
export function normalizePhone(value) {
  if (!value) return value;
  const trimmed = String(value).trim();
  if (!PHONE_LOOSE_RE.test(trimmed)) return trimmed;
  const plus = trimmed.startsWith("+") ? "+" : "";
  return plus + trimmed.replace(/\D/g, "");
}

/**
 * Returns a payload-ready identifier — trim, canonical phone.
 * Emails and usernames pass through AS TYPED: the backend's
 * /authenticate lookup is an exact match against the stored value
 * (usernames are created from the email with its original casing),
 * so lower-casing an email here made email sign-in miss while
 * username/phone sign-in worked. Do not mutate case on the client.
 */
export function normalizeIdentifier(value) {
  const v = (value || "").trim();
  const kind = identifierKindStrict(v);
  if (kind === "phone") return normalizePhone(v);
  return v;
}

/** Pretty label for the small badge / helper text. */
export function identifierLabel(kind) {
  switch (kind) {
    case "email":    return "Email";
    case "phone":    return "Phone";
    case "username": return "Username";
    default:         return "";
  }
}

/** HTML inputMode hint so phones / tablets pop the right keyboard. */
export function identifierInputMode(kind) {
  switch (kind) {
    case "email": return "email";
    case "phone": return "tel";
    default:      return "text";
  }
}

/**
 * autoComplete attribute — sends the right hint to password managers /
 * iOS / Android so saved credentials auto-fill correctly regardless of
 * which identifier the user pasted in.
 */
export function identifierAutoComplete(kind) {
  switch (kind) {
    case "email": return "email";
    case "phone": return "tel";
    default:      return "username";
  }
}
